import { useState } from "react";
import { toast } from "sonner";
import { Loader2, CheckCircle2, CalendarDays, Phone, MessageCircle } from "lucide-react";
import { SITE } from "@/lib/site";

const CONDITIONS = [
  "Breast Cancer",
  "Gynecological Cancer",
  "Head and Neck Cancer",
  "Lung Cancer",
  "Pediatric Oncology",
  "Neuro-Oncology",
  "Genito-Urinary Cancer",
  "Gastrointestinal Cancer",
  "Other / Not sure yet",
];

export const AppointmentForm = () => {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    condition: "",
    date: "",
    message: "",
  });
  const [status, setStatus] = useState("idle"); // idle | loading | done

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Please provide your name and phone number.");
      return;
    }
    setStatus("loading");
    // Simulate submission (no storage as per user choice)
    await new Promise((r) => setTimeout(r, 800));
    setStatus("done");
    toast.success("Request received. We'll contact you shortly.");
  };

  if (status === "done") {
    return (
      <div
        data-testid="appointment-success"
        className="rounded-3xl bg-white border border-slate-100 p-8 md:p-10 text-center shadow-[0_20px_50px_-30px_rgba(30,58,138,0.25)]"
      >
        <div className="mx-auto h-14 w-14 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-600">
          <CheckCircle2 className="h-7 w-7" />
        </div>
        <h3 className="mt-5 font-serif text-2xl text-slate-900">
          Thank you, {form.name.split(" ")[0]}.
        </h3>
        <p className="mt-3 text-slate-600 leading-relaxed max-w-md mx-auto">
          Your consultation request has been received. Our team will get in
          touch on your provided number to confirm the appointment.
        </p>
        <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
          <a
            href={`tel:${SITE.phoneRaw}`}
            className="inline-flex items-center justify-center gap-2 bg-blue-900 text-white font-semibold px-5 py-3 rounded-full"
          >
            <Phone className="h-4 w-4" /> Call now
          </a>
          <a
            href={`https://wa.me/${SITE.whatsapp}?text=${encodeURIComponent(SITE.whatsappMessage)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 bg-emerald-500 text-white font-semibold px-5 py-3 rounded-full"
          >
            <MessageCircle className="h-4 w-4" /> Message on WhatsApp
          </a>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="appointment-form"
      className="rounded-3xl bg-white border border-slate-100 p-6 md:p-10 shadow-[0_20px_50px_-30px_rgba(30,58,138,0.25)]"
    >
      <div className="flex items-center gap-3 mb-1">
        <div className="h-10 w-10 rounded-full bg-blue-100 text-blue-800 flex items-center justify-center">
          <CalendarDays className="h-5 w-5" />
        </div>
        <div>
          <h3 className="font-serif text-2xl text-slate-900">
            Book Appointment
          </h3>
          <p className="text-xs text-slate-500">
            We respond within one working day.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
        <Field label="Full name" required>
          <input
            data-testid="form-name"
            type="text"
            value={form.name}
            onChange={update("name")}
            required
            className={inputCls}
            placeholder="Your name"
          />
        </Field>
        <Field label="Phone number" required>
          <input
            data-testid="form-phone"
            type="tel"
            value={form.phone}
            onChange={update("phone")}
            required
            className={inputCls}
            placeholder="+91 90000 00000"
          />
        </Field>
        <Field label="Email">
          <input
            data-testid="form-email"
            type="email"
            value={form.email}
            onChange={update("email")}
            className={inputCls}
            placeholder="you@email.com"
          />
        </Field>
        <Field label="Preferred date">
          <input
            data-testid="form-date"
            type="date"
            value={form.date}
            onChange={update("date")}
            className={inputCls}
          />
        </Field>
        <div className="md:col-span-2">
          <Field label="Condition / area of concern">
            <select
              data-testid="form-condition"
              value={form.condition}
              onChange={update("condition")}
              className={inputCls}
            >
              <option value="">Select an option</option>
              {CONDITIONS.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <div className="md:col-span-2">
          <Field label="Your message">
            <textarea
              data-testid="form-message"
              value={form.message}
              onChange={update("message")}
              rows={4}
              className={inputCls}
              placeholder="Briefly share the diagnosis or reports available (optional)."
            />
          </Field>
        </div>
      </div>

      <button
        type="submit"
        data-testid="form-submit"
        disabled={status === "loading"}
        className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-blue-900 text-white font-semibold px-6 py-4 rounded-full hover:bg-blue-800 transition-colors disabled:opacity-70"
      >
        {status === "loading" ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" /> Sending…
          </>
        ) : (
          <>Request Consultation →</>
        )}
      </button>

      <p className="mt-4 text-xs text-slate-500 text-center leading-relaxed">
        By submitting, you agree to be contacted about your appointment. This
        form is for scheduling only and not for medical emergencies.
      </p>
    </form>
  );
};

const inputCls =
  "w-full rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-3 text-sm text-slate-800 placeholder:text-slate-400 focus:bg-white focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none transition";

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">
      {label} {required && <span className="text-blue-700">*</span>}
    </span>
    <div className="mt-2">{children}</div>
  </label>
);

export default AppointmentForm;
